// src/components/Pages/SmartForecast/Customer360Section.jsx
import React from "react";
import {
  Users,
  UserCheck,
  UserPlus,
  Trophy,
  PieChart,
  Activity,
  TrendingUp,
  Megaphone,
  Target,
  Zap,
  Clock,
  AlertCircle,
  Award
} from "lucide-react";
import { formatCurrency } from '../../../../utils/format';

// Data giả lập cho phần AI Marketing (vì API chưa trả về phần này)
const marketingCampaigns = [
  {
    segment: "VIP",
    target: "Duy trì sự hài lòng & Tăng giá trị",
    offer: "Tặng món mới miễn phí hoặc Cashback 5%",
    bestTime: "Cuối tuần hoặc Sinh nhật",
    icon: Trophy,
    color: "text-yellow-600",
    bg: "bg-yellow-50",
    borderColor: "border-yellow-200",
  },
  {
    segment: "Loyalty",
    target: "Tăng tần suất giao dịch (F)",
    offer: "Combo giảm 20% / Tích điểm x2",
    bestTime: "Khung giờ vàng 12:00 - 13:30",
    icon: Award,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    borderColor: "border-emerald-200",
  },
  {
    segment: "Vãng Lai",
    target: "Kéo khách quay lại lần 2",
    offer: "Voucher giảm 20k cho đơn sau",
    bestTime: "Gửi tự động sau 2 ngày mua",
    icon: Users,
    color: "text-blue-600",
    bg: "bg-blue-50",
    borderColor: "border-blue-200",
  },
  {
    segment: "Churn",
    target: "Win-back (Kéo khách trở lại)",
    offer: "Giảm sâu 50% (Tối đa 50k)",
    bestTime: "Giờ cao điểm sáng",
    icon: AlertCircle,
    color: "text-red-600",
    bg: "bg-red-50",
    borderColor: "border-red-200",
  },
];

export default function Customer360Section({
  overviewData,
  segmentData,
  demographicData,
}) {
  if (!overviewData) return null;

  const bestSegment =
    segmentData.length > 0
      ? [...segmentData].sort((a, b) => b.total_revenue - a.total_revenue)[0]
      : null;
  const totalRevenue = segmentData.reduce(
    (sum, item) => sum + item.total_revenue,
    0,
  );

  return (
    <div className="space-y-8 mt-12 animate-fade-in-up">
      {/* Title Divider */}
      <div className="relative flex py-6 items-center">
        <div className="flex-grow border-t border-slate-300"></div>
        <span className="flex-shrink-0 mx-4 text-blue-700 text-base font-black uppercase tracking-widest flex items-center gap-2 bg-blue-50 px-5 py-2 rounded-full border border-blue-200 shadow-sm">
          <Users size={20} /> CUSTOMER 360° INSIGHTS
        </span>
        <div className="flex-grow border-t border-slate-300"></div>
      </div>

      {/* === SECTION 1: OVERVIEW KPI CARDS === */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* KPI: Tổng Khách Hàng */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-between">
          <div className="flex justify-between items-start">
            <div>
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest">
                Tổng khách hàng
              </p>
              <h3 className="text-4xl font-black mt-2 text-slate-900 tabular-nums tracking-tight">
                {overviewData.total_customers}
              </h3>
            </div>
            <div className="p-3 bg-blue-50 text-blue-600 rounded-xl shadow-inner border border-blue-100">
              <Users size={24} />
            </div>
          </div>
          <div className="mt-6">
            <div className="w-full bg-slate-100 rounded-full h-3 mb-3 flex overflow-hidden shadow-inner">
              <div
                className="bg-blue-600 h-full transition-all"
                style={{ width: `${overviewData.old_rate}%` }}
              ></div>
              <div
                className="bg-emerald-500 h-full transition-all"
                style={{ width: `${overviewData.new_rate}%` }}
              ></div>
            </div>
            <div className="flex justify-between text-xs font-bold text-slate-600">
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-blue-600 shadow-sm"></span>{" "}
                Cũ ({overviewData.old_rate}%)
              </span>
              <span className="flex items-center gap-1.5">
                <span className="w-3 h-3 rounded-full bg-emerald-500 shadow-sm"></span>{" "}
                Mới ({overviewData.new_rate}%)
              </span>
            </div>
          </div>
        </div>

        {/* KPI: Khách Cũ */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-center">
          <div className="flex items-center gap-5">
            <div className="p-4 bg-blue-50 text-blue-600 rounded-xl shadow-inner border border-blue-100">
              <UserCheck size={28} />
            </div>
            <div>
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                Khách cũ quay lại
              </p>
              <h3 className="text-3xl font-black text-slate-900 tabular-nums">
                {overviewData.old_customers}{" "}
                <span className="text-base font-bold text-slate-500">
                  người
                </span>
              </h3>
            </div>
          </div>
        </div>

        {/* KPI: Khách Mới */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200 flex flex-col justify-center">
          <div className="flex items-center gap-5">
            <div className="p-4 bg-emerald-50 text-emerald-600 rounded-xl shadow-inner border border-emerald-100">
              <UserPlus size={28} />
            </div>
            <div>
              <p className="text-xs font-black text-slate-500 uppercase tracking-widest mb-1">
                Khách hàng mới
              </p>
              <h3 className="text-3xl font-black text-slate-900 tabular-nums">
                {overviewData.new_customers}{" "}
                <span className="text-base font-bold text-slate-500">
                  người
                </span>
              </h3>
            </div>
          </div>
        </div>

        {/* KPI: Nhóm VIP */}
        {bestSegment ? (
          <div className="bg-gradient-to-br from-blue-700 to-indigo-900 rounded-2xl p-6 shadow-lg text-white border border-blue-600 flex flex-col justify-between">
            <div>
              <p className="text-blue-200 text-xs font-black uppercase tracking-widest">
                Nhóm VIP Đóng góp
              </p>
              <h3 className="text-2xl font-black mt-2 flex items-center gap-2">
                <Trophy size={24} className="text-yellow-400 fill-yellow-400" />{" "}
                Nhóm {bestSegment.segment_name}
              </h3>
            </div>
            <div className="mt-4 pt-4 border-t border-white/20">
              <p className="text-blue-200 text-xs font-bold mb-1 uppercase">
                Tổng chi tiêu
              </p>
              <p className="text-3xl font-black text-yellow-400 tabular-nums tracking-tight">
                {formatCurrency(bestSegment.total_revenue)}
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-slate-100 rounded-2xl p-6 border border-slate-200 flex flex-col items-center justify-center text-slate-400 italic">
            <Trophy size={32} className="mb-2 opacity-50" />
            <p className="text-sm font-semibold">Chưa có phân loại nhóm KH</p>
          </div>
        )}
      </div>

      {/* === SECTION 2: PHÂN TÍCH NHÓM === */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Bảng Dữ Liệu Các Nhóm (Chiếm 2/3 không gian) */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-200 lg:col-span-2 overflow-hidden flex flex-col">
          <div className="p-5 border-b border-slate-100 flex items-center bg-slate-50/80">
            <h2 className="text-lg font-black text-slate-900 flex items-center gap-2">
              <PieChart size={22} className="text-blue-600" /> Phân Tích Doanh
              Thu Theo Nhóm
            </h2>
          </div>
          <div className="overflow-x-auto flex-1">
            <table className="w-full text-left text-sm min-w-[600px]">
              <thead className="bg-slate-100 text-slate-600 border-b border-slate-200">
                <tr>
                  <th className="py-4 px-5 font-black uppercase tracking-widest text-xs">
                    Phân Khúc
                  </th>
                  <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                    Số Lượng
                  </th>
                  <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                    Tổng Doanh Thu
                  </th>
                  <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                    % Tỷ Trọng
                  </th>
                  <th className="py-4 px-5 font-black uppercase tracking-widest text-xs text-right">
                    Chi Tiêu TB
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {segmentData.map((row, idx) => {
                  const Icon = row.icon || Users;
                  const revenuePercent =
                    totalRevenue > 0
                      ? ((row.total_revenue / totalRevenue) * 100).toFixed(1)
                      : 0;
                  return (
                    <tr
                      key={idx}
                      className="even:bg-slate-50 hover:bg-blue-50/60 transition-colors"
                    >
                      <td className="py-4 px-5">
                        <div className="flex items-center gap-3">
                          <div
                            className={`p-2.5 rounded-xl ${row.color} text-white shadow-md`}
                          >
                            <Icon size={18} />
                          </div>
                          <span className="font-bold text-slate-800 text-base">
                            {row.segment_name}
                          </span>
                        </div>
                      </td>
                      <td className="py-4 px-5 text-right font-bold text-slate-700 tabular-nums text-base">
                        {row.total_customers}
                      </td>
                      <td className="py-4 px-5 text-right font-black text-slate-900 tabular-nums text-lg">
                        {formatCurrency(row.total_revenue)}
                      </td>
                      <td className="py-4 px-5 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <span className="text-sm font-bold text-slate-700 w-12 text-right tabular-nums">
                            {revenuePercent}%
                          </span>
                          <div className="w-24 h-2.5 bg-slate-200 rounded-full overflow-hidden shadow-inner">
                            <div
                              className={`h-full ${row.color} rounded-full transition-all`}
                              style={{ width: `${revenuePercent}%` }}
                            ></div>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-5 text-right font-bold text-slate-600 tabular-nums text-base">
                        {formatCurrency(row.avg_ticket_size)}
                      </td>
                    </tr>
                  );
                })}
                {segmentData.length === 0 && (
                  <tr>
                    <td
                      colSpan="5"
                      className="py-8 text-center text-slate-500 font-medium"
                    >
                      Chưa có dữ liệu phân nhóm khách hàng.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Tần suất giao dịch (Chiếm 1/3) */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-8">
            <Activity size={22} className="text-blue-600" /> Tần Suất Giao Dịch
          </h2>
          <div className="space-y-7">
            {segmentData.map((row, idx) => {
              const frequency =
                row.total_customers > 0
                  ? (row.total_transactions / row.total_customers).toFixed(1)
                  : 0;
              const validFreqs = segmentData.map((r) =>
                r.total_customers > 0
                  ? r.total_transactions / r.total_customers
                  : 0,
              );
              const maxFreq = Math.max(...validFreqs) || 1;
              const percent = (frequency / maxFreq) * 100;

              return (
                <div key={idx}>
                  <div className="flex justify-between items-end mb-2.5">
                    <span className="text-sm font-bold text-slate-700">
                      {row.segment_name}
                    </span>
                    <span className="text-xl font-black text-slate-900 tabular-nums">
                      {frequency}{" "}
                      <span className="text-xs font-bold text-slate-500 lowercase">
                        lần/người
                      </span>
                    </span>
                  </div>
                  <div className="w-full bg-slate-100 rounded-full h-2.5 shadow-inner">
                    <div
                      className={`${row.color} h-2.5 rounded-full transition-all duration-500 shadow-sm`}
                      style={{ width: `${percent}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
            {segmentData.length === 0 && (
              <div className="text-slate-500 italic text-center">
                Chưa có dữ liệu
              </div>
            )}
          </div>
        </div>
      </div>

      {/* === SECTION 3: CHÂN DUNG KHÁCH HÀNG & HÀNH VI === */}
      <div className="hidden grid grid-cols-1 gap-6">
        {/* Chân Dung (Giới tính, Độ tuổi) */}
        <div className="bg-white rounded-2xl p-6 shadow-sm border border-slate-200">
          <h2 className="text-lg font-black text-slate-900 flex items-center gap-2 mb-8">
            <Users size={22} className="text-blue-600" /> Chân Dung Tiềm Năng
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Giới tính */}
            <div>
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b border-slate-100 pb-3">
                Tỷ lệ giới tính
              </h3>
              <div className="flex items-center gap-8">
                {(() => {
                  const femalePct = demographicData.gender[0].percent;
                  // FIX: Thêm mã màu tĩnh để phòng trường hợp Data không có field hex
                  const colorFemale =
                    demographicData.gender[0].hex || "#F472B6"; // pink-400
                  const colorMale = demographicData.gender[1]?.hex || "#60A5FA"; // blue-400

                  return (
                    <div
                      className="relative w-32 h-32 rounded-full flex items-center justify-center shadow-md"
                      style={{
                        background: `conic-gradient(${colorFemale} 0% ${femalePct}%, ${colorMale} ${femalePct}% 100%)`,
                      }}
                    >
                      <div className="w-24 h-24 bg-white rounded-full flex flex-col items-center justify-center shadow-inner">
                        {/* FIX: Lấy luôn số cao nhất đại diện hiển thị vào giữa vòng tròn */}
                        <span className="text-2xl font-black text-slate-900 tabular-nums">
                          {Math.max(
                            demographicData.gender[0].percent,
                            demographicData.gender[1].percent,
                          )}
                          %
                        </span>
                      </div>
                    </div>
                  );
                })()}

                <div className="space-y-5">
                  {demographicData.gender.map((g, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span
                        className={`w-5 h-5 rounded-md shadow-sm ${g.color}`}
                      ></span>
                      <span className="font-bold text-slate-700 text-base uppercase">
                        {g.name}
                      </span>
                      <span className="text-slate-900 font-black text-xl ml-3 tabular-nums">
                        {g.percent}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Độ tuổi & Thu nhập */}
            <div>
              <h3 className="text-xs font-black text-slate-400 uppercase tracking-widest mb-6 border-b border-slate-100 pb-3">
                Độ tuổi & Thu nhập (Ước tính)
              </h3>
              <div className="space-y-6">
                {demographicData.age.map((item, idx) => (
                  <div key={idx} className="flex items-center gap-4">
                    <div className="w-16 text-sm font-black text-slate-700 bg-slate-100 py-1.5 text-center rounded-lg border border-slate-200 shrink-0">
                      {item.range}
                    </div>
                    <div className="flex-1">
                      <div className="flex justify-between items-center mb-1.5">
                        <span className="font-black text-blue-700 tabular-nums">
                          {formatCurrency(item.spend)}
                        </span>
                      </div>
                      <div className="w-full bg-slate-100 rounded-full h-2.5 shadow-inner relative flex items-center">
                        <div
                          className="bg-blue-500 h-2.5 rounded-full shadow-sm transition-all"
                          style={{ width: `${item.percent}%` }}
                        ></div>
                        <span
                          className="text-xs font-bold text-slate-600 ml-2 tabular-nums absolute"
                          style={{ left: `calc(${item.percent}% + 8px)` }}
                        >
                          {item.percent}%
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* === SECTION 4: AI MARKETING RECOMMENDS === */}
      {/* Tính năng này chỉ mang tính trình diễn giao diện dựa trên Mock Data */}
      <div>
        <h2 className="text-2xl font-extrabold text-slate-900 flex items-center gap-2 mb-6">
          <Megaphone size={28} className="text-blue-600" /> AI Đề Xuất Chiến
          Dịch Marketing
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {marketingCampaigns.map((camp, idx) => {
            const Icon = camp.icon;
            return (
              <div
                key={idx}
                className={`bg-white rounded-2xl p-6 border-2 ${camp.borderColor} shadow-sm relative overflow-hidden flex flex-col hover:shadow-xl transition-all group`}
              >
                <div
                  className={`absolute top-0 right-0 w-28 h-28 ${camp.bg} rounded-bl-full opacity-60 group-hover:scale-125 transition-transform duration-500`}
                ></div>
                <div className="relative z-10 flex-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div
                      className={`p-2.5 bg-white rounded-xl shadow-sm border ${camp.borderColor}`}
                    >
                      <Icon size={24} className={camp.color} />
                    </div>
                    <h3 className="font-black text-xl text-slate-900">
                      Nhóm {camp.segment}
                    </h3>
                  </div>
                  <p className="text-sm text-slate-600 font-bold mb-6 flex items-start gap-2 h-10">
                    <Target
                      size={18}
                      className="mt-0.5 shrink-0 text-slate-400"
                    />{" "}
                    {camp.target}
                  </p>
                  <div className="space-y-4 bg-slate-50 rounded-xl p-5 border border-slate-200 shadow-inner">
                    <div className="flex gap-3 items-start">
                      <Zap
                        size={18}
                        className="text-amber-500 fill-amber-500 mt-0.5 shrink-0"
                      />
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Gợi ý Ưu đãi
                        </p>
                        <p className="text-sm font-bold text-slate-800 mt-1 leading-snug">
                          {camp.offer}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-3 items-start">
                      <Clock
                        size={18}
                        className="text-blue-500 mt-0.5 shrink-0"
                      />
                      <div>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                          Thời điểm vàng
                        </p>
                        <p className="text-sm font-bold text-slate-800 mt-1 leading-snug">
                          {camp.bestTime}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
                <button className="mt-6 w-full py-3.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-black rounded-xl transition-all shadow-md hover:shadow-lg relative z-10 uppercase tracking-wide">
                  Thiết lập Campaign
                </button>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
